import React, {createContext, useContext, useEffect, useState,} from 'react';
import {useRouteMatch} from 'react-router-dom';
import PropTypes from 'prop-types';
import {FormikProvider, useFormik} from 'formik';
import moment from "moment";
import {useDispatch} from "react-redux";
import {getInspectionOtherDetails} from '../actions/getInspectionOtherDetails';
import {pathnames as P} from "common/constants/pathnames";
import {normalizeFormValues} from "common/utils/form/normalizeFormValues";
import {showGlobalErrorMessage} from "common/actions/globalErrorMessage/showGlobalErrorMessage";
import {submitInspectionOther} from '../actions/submitInspectionOther';


const InspectionOtherUIContext = createContext('');

export function useInspectionOtherUIContext() {
  return useContext(InspectionOtherUIContext);
}

export function InspectionOtherUIProvider({children, cities, streets, updateStreets}) {
  const [loading, setLoading] = useState(false);
  const [inspectionData, setInspectionData] = useState({});

  const dispatch = useDispatch();
  const {params} = useRouteMatch(P.INSPECTION_OTHER_ID);
  const shumaId = parseInt(params.shumaId, 10);

  useEffect(() => {
    if (shumaId) {
      setLoading(true);
      Promise.resolve()
        .then(() => getInspectionOtherDetails(shumaId))
        .then(setInspectionData)
        .then(() => {
          if (inspectionData?.cityID) {
            updateStreets(inspectionData?.cityID);
          }
        })
        .catch(e => console.error(e))
        .finally(() => setLoading(false))
    }
  }, []);

  useEffect(() => {
    if (inspectionData?.cityID) {
      updateStreets(inspectionData?.cityID);
    }
  }, [inspectionData?.cityID]);

  const initialValues = {
    shumaID: inspectionData?.shumaID || shumaId || '',
    orderID: inspectionData?.orderID || '',
    gush: inspectionData?.gush || '',
    helka: inspectionData?.helka || '',
    ttHelka: inspectionData?.ttHelka || '',
    cityID: inspectionData?.cityID
      ? {label: inspectionData?.cityName, value: inspectionData?.cityID} : null,
    streetID: inspectionData?.streetID
      ? {label: inspectionData?.streetName, value: inspectionData?.streetID} : null,
    houseNumber: inspectionData?.houseNumber || '',
    officialDate: (inspectionData?.officialDate)
      ? new Date(inspectionData?.officialDate) : null,
    averageSqmPrice: inspectionData?.averageSqmPrice || '',
    estimatePriceFinal: inspectionData?.estimatePriceFinal || '',
    landRegisteredLotArea: inspectionData?.landRegisteredLotArea || '',
    houseRegisteredArea: inspectionData?.houseRegisteredArea || '',
    houseRoomCount: inspectionData?.houseRoomCount || '',
    floor: inspectionData?.floor || '',
    floorCount: inspectionData?.floorCount || '',
    buildingAge: inspectionData?.buildingAge || '',
    parking: Boolean(inspectionData?.parking) || false,
    elevator: Boolean(inspectionData?.elevator) || false,
    yard: Boolean(inspectionData?.yard) || false,
    storage: Boolean(inspectionData?.storage) || false,
    roof: Boolean(inspectionData?.roof) || false,
    customers: inspectionData?.customers || [],
  };

  const handleCityChange = formik => selected => {
    const {values: {cityID}, setFieldValue} = formik;
    if (!cityID || cityID.value !== selected.value) {
      setFieldValue('streetID', null);
      if (Boolean(selected.value)) {
        updateStreets(selected.value);
      } else {
        handleCityReset(formik)();
      }
    }
  };
  const handleCityReset = formik => () => {
    const {setFieldValue} = formik;
    setFieldValue('streetID', null);
    setFieldValue('cityID', null);
  };

  const submitWithoutFormReinitialization = values => {
    const {
      gush,
      helka,
      ttHelka,
      cityID,
      streetID,
      houseNumber,
      officialDate,
      averageSqmPrice,
      estimatePriceFinal,
      landRegisteredLotArea,
      houseRegisteredArea,
      houseRoomCount,
      floor,
      floorCount,
      buildingAge,
      parking,
      elevator,
      yard,
      storage,
      roof,
      customers
    } = normalizeFormValues(values);
    const data = {
      shumaID: shumaId,
      gush,
      helka,
      ttHelka,
      cityID,
      streetID,
      houseNumber,
      officialDate: officialDate
        ? moment(officialDate).format('YYYY-MM-DD') : null,
      averageSqmPrice,
      estimatePriceFinal,
      landRegisteredLotArea,
      houseRegisteredArea,
      houseRoomCount,
      floor,
      floorCount,
      buildingAge,
      parking,
      elevator,
      yard,
      storage,
      roof,
      customers: customers && customers.map(item => item.customerID)
    };

    return submitInspectionOther(data)
  };
  const handleDataSubmit = (values) => {
    Promise.resolve()
      .then(() => submitWithoutFormReinitialization(values))
      .then(() => dispatch(showGlobalErrorMessage('הנתונים נשמרו בהצלחה')))
      .catch((err) => dispatch(showGlobalErrorMessage(err.message)));
  };

  const formik = useFormik({
    initialValues,
    onSubmit: () => {
    },
    enableReinitialize: true,
  });

  const value = {
    orderID: inspectionData?.orderID,
    shumaId,
    firmShumaID: inspectionData?.firmShumaID,
    formik,
    loading,
    options: {
      cities,
      streets
    },
    cityChangeProps: {
      onChange: handleCityChange(formik),
      onReset: handleCityReset(formik)
    },
    handleDataSubmit
  };

  return (
    <InspectionOtherUIContext.Provider value={value}>
      <FormikProvider value={formik}>
        {children}
      </FormikProvider>
    </InspectionOtherUIContext.Provider>
  );
}

InspectionOtherUIProvider.propTypes = {
  children: PropTypes.node,
};
